
import React from 'react';
import { useAuth } from '@/contexts/AuthContext';
import MainLayout from '@/components/layouts/MainLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ThemeToggle } from '@/components/theme/ThemeToggle';
import PasswordUpdateForm from '@/components/auth/PasswordUpdateForm';
import { useAuthor } from '@/hooks/useAuthors';
import { Skeleton } from '@/components/ui/skeleton';

const AuthorAccountSettings = () => {
  const { user } = useAuth();
  const { data: authorData, isLoading } = useAuthor(user?.id || '');
  
  if (isLoading) {
    return (
      <MainLayout>
        <div className="container mx-auto max-w-3xl py-6">
          <h1 className="text-2xl font-bold mb-6 text-black dark:text-white">Account Settings</h1>
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <Skeleton className="h-6 w-48" />
                <Skeleton className="h-4 w-64" />
              </CardHeader>
              <CardContent className="space-y-4">
                <Skeleton className="h-20 w-full" />
              </CardContent>
            </Card>
          </div>
        </div>
      </MainLayout>
    );
  }
  
  return (
    <MainLayout>
      <div className="container mx-auto max-w-3xl py-6">
        <h1 className="text-2xl font-bold mb-6 text-black dark:text-white">Account Settings</h1>
        
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Profile Information</CardTitle>
            <CardDescription>Your account profile information (read-only)</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name</Label>
                  <Input 
                    id="name" 
                    value={authorData?.name || ''} 
                    readOnly 
                    className="bg-gray-50 dark:bg-gray-800"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <Input 
                    id="email" 
                    value={authorData?.email || ''} 
                    readOnly 
                    className="bg-gray-50 dark:bg-gray-800"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input 
                    id="phone" 
                    value={authorData?.phone || 'Not provided'} 
                    readOnly 
                    className="bg-gray-50 dark:bg-gray-800"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="role">Account Role</Label>
                  <Input 
                    id="role" 
                    value={authorData?.role || 'author'} 
                    readOnly 
                    className="bg-gray-50 dark:bg-gray-800 capitalize"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="address">Address</Label>
                <Input 
                  id="address" 
                  value={authorData?.address || 'Not provided'} 
                  readOnly 
                  className="bg-gray-50 dark:bg-gray-800"
                />
              </div>
              
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="tax-percentage">Tax Percentage</Label>
                  <Input 
                    id="tax-percentage" 
                    value={`${authorData?.tax_percentage || 10}%`} 
                    readOnly 
                    className="bg-gray-50 dark:bg-gray-800"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="books-count">Published Books</Label>
                  <Input 
                    id="books-count" 
                    value={authorData?.books?.length || 0} 
                    readOnly 
                    className="bg-gray-50 dark:bg-gray-800"
                  />
                </div>
              </div>
              
              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-md">
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  <strong>Note:</strong> To update your profile information, please contact your administrator or use the admin panel if you have admin access.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Appearance</CardTitle>
            <CardDescription>Customize how the application looks</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium">Theme</h3>
                <p className="text-sm text-muted-foreground">Select your preferred theme</p>
              </div>
              <ThemeToggle />
            </div>
          </CardContent>
        </Card>
        
        <PasswordUpdateForm />
      </div>
    </MainLayout>
  );
};

export default AuthorAccountSettings;
